/**
 * Tutorial System Types
 * Defines the structure for the in-app walkthrough feature
 */

import type { FeatureFlags } from "../../config/featureFlags";

export type TutorialTier = "brief" | "detailed";
export type TutorialRole = "MarketingOnly" | "U" | "Admin";

export type TutorialArea =
  | "dashboard"
  | "ai-copilot"
  | "calendar"
  | "settings"
  | "marketing"
  | "reputation"
  | "groups"
  | "booking-engine";

export type TutorialStepPosition = "top" | "bottom" | "left" | "right" | "center";

export interface TutorialStep {
  /** Unique identifier for the step */
  id: string;
  /** Which area this step belongs to */
  area: TutorialArea;
  /** Title displayed in the tutorial card */
  title: string;
  /** Description/instructions for this step */
  description: string;
  /** CSS selector or data attribute to target for spotlight */
  targetSelector: string;
  /** Preferred position of tutorial card relative to target */
  cardPosition: TutorialStepPosition;
  /** Route path where this step should be shown (e.g., "/dashboard") */
  route: string;
  /** Optional: Wait for element to be visible before showing step */
  waitForElement?: boolean;
  /** Optional: Scroll target into view */
  scrollIntoView?: boolean;
  /** Optional: Skip this step if the target element is not found in the DOM */
  skipIfTargetMissing?: boolean;
  /** Optional: Skip this step if the given selector IS found in the DOM (inverse of skipIfTargetMissing) */
  skipIfElementExists?: string;
  /** Which tour this step belongs to: the 2-minute welcome tour or an area deep-dive */
  tier: TutorialTier;
  /** Roles that see this step. Omitted = all roles (area availability still applies) */
  roles?: TutorialRole[];
  /** Step hidden unless this feature flag is enabled */
  featureFlag?: keyof FeatureFlags;
  /** Chattier spoken script for TTS narration; falls back to description */
  narrationText?: string;
  /** Force the chatbot panel open/closed when this step shows */
  chatbotState?: "open" | "closed";
  /** Which chat tab the step wants active; applied only when the account has Agent mode */
  chatMode?: "chat" | "agent";
}

export interface TutorialAreaConfig {
  /** Unique identifier for the area */
  id: TutorialArea;
  /** Display name shown in area selection modal */
  label: string;
  /** Description of what users will learn in this area */
  description: string;
  /** Icon name from lucide-react */
  icon: string;
  /** Whether this area is available based on user role */
  isAvailable: boolean;
}

export interface TutorialState {
  /** Whether the tutorial is currently active */
  isActive: boolean;
  /** Current step index in the filtered steps array */
  currentStepIndex: number;
  /** Which areas the user selected to include */
  selectedAreas: TutorialArea[];
  /** Whether the user has completed the tutorial at least once */
  hasCompletedBefore: boolean;
  /** Whether area selection modal is open */
  showAreaSelection: boolean;
}

export interface TutorialContextType extends TutorialState {
  /** Start the tutorial with selected areas */
  startTutorial: (areas: TutorialArea[]) => void;
  /** Move to next step */
  nextStep: () => void;
  /** Move to previous step */
  previousStep: () => void;
  /** Skip current step */
  skipStep: () => void;
  /** Exit tutorial completely */
  exitTutorial: () => void;
  /** Open area selection modal */
  openAreaSelection: () => void;
  /** Close area selection modal */
  closeAreaSelection: () => void;
  /** Get all available tutorial steps */
  getAllSteps: () => TutorialStep[];
  /** Get filtered steps based on selected areas */
  getFilteredSteps: () => TutorialStep[];
  /** Get current step object */
  getCurrentStep: () => TutorialStep | null;
  /** Calculate total number of steps based on selections */
  getTotalSteps: () => number;
}

export const TUTORIAL_STORAGE_KEYS = {
  completed: "app_tutorial_completed_v1",
  progress: "app_tutorial_current_step",
  selectedAreas: "app_tutorial_selected_areas",
  briefCompleted: "brief_tour_completed_v1",
  welcomeDismissed: "welcome_tour_dismissed_v1",
  narrationEnabled: "tutorial_narration_enabled",
} as const;
